import { tv } from 'tailwind-variants';
import { ALL_THEMES } from 'lib/context/ThemeContext';

const themeVariants = ALL_THEMES.reduce(
  (acc, curr) => ((acc[curr] = {}), acc),
  {} as Record<string, object>
);

export const tailwindVariants = tv({
  slots: {
    iconBg: [],
    base: [
      'flex',
      'flex-col',
      'py-themes-general-surface-spacing-small-padding-y',
      'px-themes-general-surface-spacing-small-padding-x',
      'md:py-themes-general-surface-spacing-large-padding-y',
      'md:px-themes-general-surface-spacing-large-padding-x',
      'bg-components-category-card-listing-color-bg',
    ],
    TitleContainer: [
      'flex',
      'flex-col',
      'align-center',
      'items-stretch',
      'md:gap-components-top-title-spacing-large-body-margin-bottom',
      'md:pb-components-top-title-spacing-large-margin-bottom',
    ],
    Title: [
      'text-components-category-card-listing-color-title',
      'md:font-header-large-large',
      'md:text-header-large-large',
      'md:leading-header-large-large',
      'md:tracking-header-large-large',
      'font-header-small-large',
      'text-header-small-large',
      'leading-header-small-large',
      'tracking-header-small-large',
    ],
    headerContainer: [
      'flex',
      'justify-between',
      'mb-themes-general-surface-spacing-small-margin-bottom',
      'w-full',
      'md:mb-themes-general-surface-spacing-large-margin-bottom',
      'flex-col',
      'md:flex-row',
    ],
    headerTitleContainer: ['flex', 'flex-col', 'lg:mb-0'],
    headerTitleText: [
      'font-header-small-large',
      'text-header-small-large',
      'leading-header-small-large',
      'text-components-category-card-listing-color-title',
      'md:font-header-large-large',
      'md:leading-header-large-large',
      'md:text-header-large-large',
    ],
    headerDescriptionText: [
      'font-bodySans-small',
      'leading-bodySans-small',
      'text-bodySans-small',
      'md:font-bodySans-medium',
      'md:leading-bodySans-medium',
      'md:text-bodySans-medium',
      'md:mt-themes-general-surface-spacing-large-title-margin-bottom',
      'mt-themes-general-surface-spacing-large-title-margin-bottom',
      'text-components-category-card-listing-color-copy',
      'max-md:mb-themes-general-surface-spacing-large-body-margin-bottom',
    ],
    ctaContainer: ['flex', 'items-center'],
    cardContainer: [
      'grid',
      'grid-cols-1',
      'md:grid-cols-2',
      'gap-themes-general-surface-spacing-large-cards-space-between',
    ],
    cardWrapper: ['flex', 'w-full'],
    cardTitle: [
      'font-header-medium-xSmall',
      'leading-header-medium-xSmall',
      'text-header-medium-xSmall',
      'md:font-header-large-xSmall',
      'md:leading-header-large-xSmall',
      'md:text-header-large-xSmall',
      'text-components-category-card-color-title',
      'md:tracking-cardSide',
      'tracking-cardSideTighter',
    ],
    card: [
      'flex',
      'w-full',
      'bg-components-category-card-color-bg',
      'border-solid',
      'border-components-category-card-color-stroke',
      'border-2',
      'flex-row',
      'gap-0',
      'items-start',
      'justify-start',
      'relative',
      'group',
      'overflow-hidden',
      'rounded-themes-spacing-radius-large-card',
      'hover:border-components-category-card-color-stroke-hover',
    ],
    cardContent: [
      'flex',
      'flex-row',
      'gap-spacing-space-between-large-2',
      'items-center',
      'justify-start',
      'relative',
    ],
    cardContentWrapper: [
      'bg-components-category-card-color-content-bg',
      'p-spacing-spacing-4',
      'flex',
      'flex-row',
      'gap-spacing-padding-large-3',
      'items-center',
      'justify-start',
      'self-stretch',
      'flex-1',
      'relative',
    ],
    cardMediaContainer: ['flex-1', 'relative', 'overflow-hidden'],
    cardMediaWrapper: [
      'p-2',
      'flex',
      'flex-row',
      'gap-2',
      'items-center',
      'justify-center',
      'md:w-2/5',
      'w-1/3',
      'h-full',
      'relative',
      'overflow-hidden',
      'group-hover:scale-105',
      'group-hover:transition',
      'group-hover:duration-500',
      'scale-100',
      'duration-500',
      'transition',
    ],
    cardIcon: ['fill-components-category-card-color-title'],
    cardSVG: [
      'absolute',
      'transition-transform',
      'ease-in',
      'translate-y-[0]',
      'md:group-hover:translate-y-[2%]',
      'md:group-hover:transition',
      'md:group-hover:duration-500',
      'md:duration-400',
      'transition',
      'w-[100%]',
      'md:w-auto',
      '[&_svg]:w-auto',
      '[&_svg]:h-auto',
    ],
    legalDisclaimerText: ['self-start', 'mt-spacing-spacing-5'],
  },
  variants: {
    alignCTA: {
      BottomCenter: {
        ctaContainer: [
          'align-center',
          'flex',
          'mt-themes-general-surface-spacing-small-button-margin-top',
          'md:mt-themes-general-surface-spacing-large-button-margin-top',
          'w-full',
          'md:[&>a]:w-auto',
          '[&>a]:w-full',
          'justify-center',
        ],
        headerTitleContainer: ['w-full', 'text-center'],
      },
      TopRight: {
        ctaContainer: ['flex', 'items-center'],
        headerTitleContainer: ['md:mr-themes-general-surface-spacing-large-body-margin-right'],
      },
    },
    size: {
      small: {
        cardSVG: ['md:hidden', 'block'],
      },
      large: {
        cardSVG: ['md:block', 'hidden'],
      },
    },
    brand: {
      ...themeVariants,
      Ziploc: {
        cardSVG: ['right-[58%]', 'md:top-[-13%]', 'md:right-[-87%]'],
      },
      Off: {
        cardSVG: ['right-[40%]', 'md:right-[-50%]'],
      },
      Autan: {
        cardSVG: ['right-[60%]', 'md:right-[-50%]'],
      },
      AutanDefense: {
        cardSVG: ['top-[-2%]'],
      },
      OffDefense: {
        cardSVG: ['top-[-2%]'],
      },
      Glade: {
        cardSVG: ['top-[-2%]', 'left-[0%]'],
        legalDisclaimerText: ['!text-components-category-card-listing-color-copy'],
      },
      Raid: {
        cardSVG: ['top-[-2%]', 'left-[0%]'],
      },
      Drano: {
        cardSVG: ['md:-top-[60%]', '-top-[130%]', 'left-[0%]'],
      },
      Baygon: {
        cardSVG: ['!w-[132%]', 'top-[0%]', 'left-[-15%]', 'rotate-[273deg]'],
      },
      Pyrel: {
        cardSVG: ['bottom-[-20%]', 'md:bottom-[-40%]'],
      },
      Pledge: {
        cardSVG: [
          'max-md:right-[50%]',
          'max-md:top-0',
          'max-md:[&_svg]:w-[140%]',
          'md:right-[-10%]',
        ],
      },
      Lysoform: {
        cardSVG: ['rotate-180'],
      },
      PIDS: {
        card: ['min-h-[56px]', 'md:min-h-[120px]'],
      },
    },
    grid: {
      '2column': {
        cardContainer: ['grid', 'grid-cols-1', 'md:grid-cols-2'],
      },
      '3column': {
        cardContainer: ['grid', 'grid-cols-1', 'md:grid-cols-3'],
      },
      '4column': {
        cardContainer: ['grid', 'grid-cols-1', 'md:grid-cols-4'],
      },
    },
  },
  compoundVariants: [
    {
      brand: 'Ziploc',
      grid: '2column',
      size: 'large',
      className: ['md:left-auto'],
    },
    {
      brand: 'Off',
      grid: '2column',
      size: 'large',
      className: ['lg:bottom-auto', 'lg:-ml-[0px]', 'lg:top-0', 'lg:w-[165%]', 'md:bottom-[52%]'],
    },
    {
      brand: 'Off',
      grid: '4column',
      size: 'large',
      className: ['lg:bottom-[55%]', 'md:bottom-[63%]'],
    },
    { brand: 'Baygon', grid: '4column', size: 'large', className: ['-top-[1%]'] },
    {
      brand: 'Baygon',
      grid: '2column',
      size: 'large',
      className: ['!-top-[10%]', '!left-[-40px]', '!w-full'],
    },
    {
      brand: 'Pyrel',
      grid: '2column',
      size: 'large',
      className: ['top-[-30%]'],
    },
    { brand: 'Lysoform', grid: '2column', size: 'large', className: ['w-[120%]', '-top-[25%]'] },
  ],
});
